import { withTransaction } from '@elastic/apm-rum-react'
import IconsEnum from '@enums/icons.enum'
import { Button, Campaign, EdnaService, LogService, Media, MediaPreApproval, Selector, Tag, ToastHelper, UserInDetails } from '@hub/shared'
import { SqLoader, SqVideoHelper } from '@squidit/react-css'
import ContentByProfileComponent from '@src/components/content-by-profile/content-by-profile.component'
import MediaPreApprovalCard from '@src/components/media-pre-approval-card/media-pre-approval-card.component'
import ModalFilter from '@src/components/modal-filter/modal-filter.component'
import OverlayApproveContent, { MapFiles } from '@src/components/overlay-approve-content/overlay-approve-content.component'
import heic2any from 'heic2any'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DosDontsCampaignComponent from '../../components/dos-donts/dos-donts-campaign.component'
import './approve.page.scoped.scss'

interface Props {
  campaign: Campaign
  userInCampaign: UserInDetails
}

interface State {
  loading?: boolean
  mediasList?: MediaPreApproval[]
  openFilter?: boolean
  mediasSelected?: Media[]
}

interface StateOverlay {
  newContentList?: File[]
  typeMedia: string
  description?: string
  mediaSelected: MediaPreApproval
  isEdit: boolean
  openNewContent?: boolean
}

const ApprovePageComponent = ({ campaign, userInCampaign }: Props) => {
  const ednaService = useMemo(() => new EdnaService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const iconsEnum = useMemo(() => IconsEnum, [])
  const sqVideoHelper = useMemo(() => new SqVideoHelper(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Approve Page')

  const [state, setState] = useState<State>({
    mediasList: [],
    openFilter: false,
    loading: false,
  })

  const [stateOverlay, setStateOverlay] = useState<StateOverlay>({
    newContentList: [],
    typeMedia: '',
    description: '',
    mediaSelected: null,
    isEdit: true,
    openNewContent: false,
  })

  const onCloseOverlay = () => {
    setStateOverlay({
      newContentList: [],
      typeMedia: '',
      description: '',
      mediaSelected: null,
      isEdit: true,
      openNewContent: false,
    })
  }

  const [filterSelected, setFilterSelected] = useState({
    review: false,
    underAnalysis: false,
    approved: false,
  })
  const [newFilterSelected, setNewFilterSelected] = useState(filterSelected)

  const [t] = useTranslation('approvePage')

  const filterOptions = useMemo(
    () => [
      {
        value: true,
        name: t('filters.review'),
        icon: 'fa-solid fa-diamond-exclamation',
        colorIcon: 'var(--yellow)',
        key: 'review',
      },
      {
        value: true,
        name: t('filters.underAnalysis'),
        icon: 'fa-solid fa-circle-check',
        colorIcon: 'var(--yellow)',
        key: 'underAnalysis',
      },
      {
        value: true,
        name: t('filters.approved'),
        icon: 'fa-solid fa-circle-check',
        colorIcon: 'var(--green)',
        key: 'approved',
      },
    ],
    [t]
  )

  const handleChange = useCallback(
    (name: string) => {
      setNewFilterSelected((prevState) => ({ ...prevState, [name]: !prevState[name] }))
    },
    [setNewFilterSelected]
  )

  const rulesConfigMap = useMemo(
    () => [
      {
        icon: <i className={`${iconsEnum.post}`}></i>,
        label: t('makePosts'),
        value: campaign?.activation?.postsCount || 0,
      },
      {
        icon: <i className={`${iconsEnum.stories}`}></i>,
        label: t('makeStories'),
        value: campaign?.activation?.storiesCount || 0,
      },
      {
        icon: <i className={`${iconsEnum.reels}`}></i>,
        label: t('makeReels'),
        value: campaign?.activation?.reelsCount || 0,
      },
      {
        icon: <i className={`${iconsEnum.tweet}`}></i>,
        label: t('makeTweet'),
        value: campaign?.activation?.tweetsCount || 0,
      },
      {
        icon: <i className={`${iconsEnum.retweet}`}></i>,
        label: t('makeRetweet'),
        value: campaign?.activation?.tweetsRetweetsCount || 0,
      },
    ],
    [campaign, t, iconsEnum]
  )

  const convertMedias = useCallback(
    async (medias: MediaPreApproval[]) => {
      const modifiedMedias = await Promise.all(
        medias?.map(async (media) => {
          const newMedia = { ...media }
          newMedia.medias = await Promise.all(
            newMedia?.medias?.map(async (m) => {
              if (m?.link.endsWith('.heic') || m?.link.endsWith('.heif')) {
                try {
                  const response = await fetch(m?.link)
                  const blob = await response.blob()
                  const newLink = (await heic2any({ blob, toType: 'image/jpeg', quality: 1 })) as Blob
                  m.link = URL.createObjectURL(newLink)
                } catch (error) {
                  if (!error?.message?.includes('Image is already browser readable')) {
                    logService.error({ message: 'Failed to convert heic to jpeg', error })
                    toast.error(t('errors.unableToConvertFile'), {
                      closeButton: true,
                      className: 'full',
                      position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
                      duration: 3600000,
                      notOverClick: true,
                      immediately: true,
                    })
                  }
                }
              }

              return {
                ...m,
                link: m.link,
              }
            })
          )

          return newMedia
        })
      )

      return modifiedMedias
    },
    [logService, toast, t]
  )

  const getContents = useCallback(() => {
    setState((prevState) => ({ ...prevState, loading: true }))
    return ednaService
      .getMediaContents({ idCampaign: campaign._id, socialId: userInCampaign?.idNetwork })
      .then(async (medias) => await convertMedias(medias))
      .then((medias) => setState((prevState) => ({ ...prevState, mediasList: medias })))
      .catch((error) => {
        logService.error({
          message: 'Failed to get medias list on pre approved page',
          error,
        })
        toast.error(t('errors.unableGetContent'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => setState((prevState) => ({ ...prevState, loading: false })))
  }, [campaign, ednaService, convertMedias, logService, toast, t, userInCampaign?.idNetwork])

  const closeModal = (confirm: boolean) => {
    if (confirm) {
      setFilterSelected({ ...newFilterSelected })
    } else {
      setNewFilterSelected({ ...filterSelected })
    }
    setState((prevState) => ({ ...prevState, openFilter: false }))
  }

  const editMedia = async (mediaSelected: MediaPreApproval) => {
    return await Promise.all(mediaSelected?.medias?.map((file) => convertUrlToFile(file?.link)))
      .then((response) => {
        setStateOverlay((prevState) => ({
          ...prevState,
          newContentList: response,
          typeMedia: mediaSelected?.type,
          description: mediaSelected?.caption,
          mediaSelected,
          isEdit: true,
          openNewContent: true,
        }))
      })
      .catch((error) => {
        logService.error({ message: 'Unable to edit media', error })
        toast.error(t('errors.unableEdit'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
  }

  const mapFiles = (files): MapFiles[] => {
    return files?.map((file) => ({
      ext: file.name.substring(file.name.lastIndexOf('.') + 1, file?.name?.length),
      contentType: file.type,
      fileName: file.name,
    }))
  }

  const mapSignedUrlsToPromises = (signedUrlArray, files) => {
    return signedUrlArray?.map((signedUrlItem, index) => {
      return fetch(signedUrlItem.postUrl.signedURL, {
        method: 'PUT',
        body: files[index],
      })
    })
  }

  const uploadFiles = useCallback(
    async (signedUrlArray, files) => {
      try {
        const transformedToPromises = mapSignedUrlsToPromises(signedUrlArray, files)

        await Promise.all(transformedToPromises)
      } catch (error) {
        logService.error({ message: 'Unable to upload files', error })
        toast.error(t('errors.unableUpload'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      }
    },
    [logService, t, toast]
  )

  const createThumbnailVideo = (videoFile) => {
    return new Promise((resolve) => {
      const response = {} as any
      const videoThumb = document.createElement('video')
      videoThumb.src = URL.createObjectURL(videoFile)
      videoThumb?.addEventListener(
        'progress',
        () => {
          videoThumb.currentTime = 1
          setTimeout(() => {
            const canvas = document.createElement('canvas')
            canvas.width = videoThumb.videoWidth
            canvas.height = videoThumb.videoHeight
            canvas.getContext('2d').drawImage(videoThumb, 0, 0, canvas.width, canvas.height)
            const image = canvas.toDataURL()
            response.data = image
            response.url = videoThumb.src
            resolve(response)
          }, 1000)
        },
        { once: true }
      )
    })
  }

  const dataURLtoFile = (dataurl) => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr?.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  }

  const videoThumbnail = useCallback(
    async (file) => {
      try {
        const { data }: any = await createThumbnailVideo(file)
        const fileName = file.name.split('.')[0]
        const base64ToFile = dataURLtoFile(data) as any
        base64ToFile.name = fileName

        const payloadThumbnail = {
          ext: 'png',
          contentType: 'image/png',
          fileName: fileName,
        }
        const response = await ednaService.generateSignedURL(campaign._id, [payloadThumbnail])

        await fetch(response[0].postUrl.signedURL, {
          method: 'PUT',
          body: base64ToFile,
        })

        return response[0].postUrl.publicURL
      } catch (error) {
        logService.error({ message: 'Unable to get video thumbnail', error })
        toast.error(t('errors.unableThumbnail'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      }
    },
    [campaign._id, ednaService, logService, t, toast]
  )

  const mapContentsToApprove = useCallback(
    async (signedUrlArray, content, contentId, requiresConversion = false) => {
      const mappedMedias = await Promise.all(
        signedUrlArray?.map(async (signedUrlItem, index) => {
          const mediaType = signedUrlItem.file.contentType.split('/')[0] || null
          let thumbnail = signedUrlItem.postUrl.publicURL
          let codecName = null

          if (mediaType === 'video') {
            thumbnail = await videoThumbnail(content.files[index])
            codecName = (await sqVideoHelper?.getCodecName(content.files[index])) || null
          }

          return {
            link: signedUrlItem.postUrl.publicURL,
            thumbnail,
            type: mediaType || null,
            codecName,
          }
        })
      )

      return {
        influencerContentToApprove: {
          ...(contentId ? { _id: contentId } : {}),
          caption: content.legend,
          idCampaign: campaign._id,
          medias: mappedMedias,
          squidId: userInCampaign?.squidId,
          type: content.type,
          socialId: userInCampaign?.idNetwork,
          stage: 'before',
          requiresConversion,
        },
      }
    },
    [campaign._id, videoThumbnail, userInCampaign, sqVideoHelper]
  )

  const submitContentToApprove = useCallback(
    async (signedUrlArray, content, contentId = null, requiresConversion = false) => {
      try {
        const payload = await mapContentsToApprove(signedUrlArray, content, contentId, requiresConversion)

        if (contentId) {
          return ednaService.putMediaContent(payload)
        }

        return ednaService.postMediaContent(payload)
      } catch (error) {
        logService.error({ message: 'Unable to submit content for approval', error })
        toast.error(t('errors.unableSubmitContent'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      }
    },
    [ednaService, mapContentsToApprove, logService, t, toast]
  )

  const handleSubmit = async (medias, contentId = null, requiresConversion = false) => {
    await Promise.all(
      medias?.map(async (media) => {
        const response = await ednaService.generateSignedURL(campaign._id, mapFiles(media.files))
        await uploadFiles(response, media.files)
        await submitContentToApprove(response, media, contentId, requiresConversion)
      })
    )

    await getContents()
  }

  const convertMedia = async (mediaSelected: MediaPreApproval) => {
    return await Promise.all(mediaSelected?.medias?.map((file) => convertUrlToFile(file?.link)))
      .then((response) => {
        handleSubmit([{ files: response, legend: mediaSelected?.caption, type: mediaSelected?.type }], mediaSelected?._id, true)
      })
      .catch((error) => {
        logService.error({ message: 'Unable to convert media', error })
        toast.error(t('errors.unableConvertMedia'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
        throw error
      })
  }

  const getFileLinkType = (url) => {
    const getFormat = url?.substring(url?.lastIndexOf('.') + 1)

    const videoFormats = {
      mp4: 'video/mp4',
      mov: 'video/mov',
    }

    return ['mp4', 'mov'].includes(getFormat) ? videoFormats[getFormat] : `image/${getFormat}`
  }

  const convertUrlToFile = useCallback(async (url) => {
    if (url) {
      const response = await fetch(url)
      const fileFormat = getFileLinkType(url)

      return new File([await response.blob()], url?.substring(url.lastIndexOf('/') + 1), {
        type: fileFormat,
      })
    }
  }, [])

  const filter = (
    <ModalFilter
      header={t('filterByState')}
      open={state?.openFilter}
      openModal={() => setState((prevState) => ({ ...prevState, openFilter: true }))}
      closeModal={(value) => closeModal(value)}
    >
      <div className="options-list my-4">
        {filterOptions?.map(({ value, name, icon, colorIcon, key }, index) => (
          <Selector
            name="status-campaign-filter-selector-option"
            id={`status-campaign-filter-selector-option-${key}-${index}`}
            wrapperClass="justify-content-space-between mb-3"
            key={key}
            value={value}
            checked={value && newFilterSelected[key]}
            onChange={() => handleChange(key)}
            type={'checkbox'}
            errorSpan={false}
            leftLabel={
              <div className="display-flex">
                <i className={`${icon} mr-2`} style={{ width: 22, fontSize: 18, color: colorIcon }}></i>
                <span className="status-campaign-filter-selector-text">{name}</span>
              </div>
            }
          />
        ))}
      </div>
    </ModalFilter>
  )

  const campaignTagRule = (
    <div className="wrapper-rule-tags">
      {userInCampaign?.scope ? (
        <ContentByProfileComponent
          title={t('rules')}
          scope={userInCampaign?.scope}
          className="p-0"
          style={{ backgroundColor: 'var(--background_secondary)', borderTop: 'none' }}
        />
      ) : (
        <div>
          <h5 className="m-0">{t('rules')}</h5>
          <div className="rules-tags">
            {rulesConfigMap?.map((rule, index) =>
              !!rule?.value ? (
                <Tag key={index} icon={rule?.icon} labels={[rule?.label, <strong>{`${rule?.value} ${t('atLeast')}`}</strong>]} />
              ) : null
            )}
          </div>
        </div>
      )}
      <DosDontsCampaignComponent campaign={campaign} />
      <Button width="100%" className="mt-2" onClick={() => setStateOverlay((prevState) => ({ ...prevState, openNewContent: true }))}>
        {t('newContent')}
      </Button>
    </div>
  )

  const addContentCard = (
    <div className="new-content-container">
      {campaignTagRule}
      <OverlayApproveContent
        campaign={campaign}
        open={stateOverlay?.openNewContent}
        setOpen={(open: boolean) => setStateOverlay((prevState) => ({ ...prevState, openNewContent: open }))}
        mediaSelected={stateOverlay?.mediaSelected}
        newContentList={stateOverlay?.newContentList}
        onCloseOverlay={onCloseOverlay}
        mapFiles={mapFiles}
        uploadFiles={uploadFiles}
        submitContentToApprove={submitContentToApprove}
        getContents={getContents}
      />
    </div>
  )

  const campaignList = (
    <div className="campaigns-list">
      {state?.mediasList?.length === 0 ? (
        <div className="empty-list text-bold">{t('noContent')}</div>
      ) : (
        <div className="wrapper-campaigns-list">
          <div className="header-list py-2">
            <h5 className="m-0">{t('contentSent')}</h5>
            {filter}
          </div>
          <div className="list">
            {state?.mediasList
              ?.filter?.((media) => {
                const filters = Object.keys(filterSelected)?.filter((key) => filterSelected[key])
                if (filters?.length > 0) {
                  const listFilters = []
                  if (filters.includes('approved')) {
                    listFilters.push('finalApproved')
                  }
                  if (filters.includes('review')) {
                    listFilters.push('finalReproved')
                  }
                  if (filters.includes('underAnalysis')) {
                    listFilters.push('inprogress')
                    listFilters.push('pending')
                  }
                  return listFilters.includes(media?.status)
                }
                return true
              })
              ?.map((media) => (
                <MediaPreApprovalCard media={media} key={media?._id} editMedia={editMedia} convertMedia={convertMedia} />
              ))}
          </div>
        </div>
      )}
    </div>
  )

  useEffect(() => {
    setState((prevState) => ({ ...prevState, loading: true }))
    getContents()
      .catch((error) => {
        logService.error({
          message: 'Failed to get medias list on pre approved page',
          error,
        })
        toast.error('Failed to get campaign list', {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        setState((prevState) => ({ ...prevState, loading: false }))
      })
  }, [getContents, toast, logService])

  return (
    <div className="content-approve">
      {addContentCard}
      {state?.loading ? <SqLoader size="big" /> : campaignList}
    </div>
  )
}

export default withTransaction('ApprovePageComponent', 'component')(ApprovePageComponent)
